/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  DegreeEvent_AssignmentProgressState as AssignmentProgressState,
  DegreeEvent_AssignmentSubmissionType as AssignmentSubmissionType,
} from '__generated__/graphql-types';

import { MED_DATETIME_NO_YEAR_DISPLAY, TIME_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { Theme } from '@coursera/cds-core';
import { Link, useTheme } from '@coursera/cds-core';
import { SuccessFilledIcon } from '@coursera/cds-icons';

import { getItemTypePrefixTranslationByTypeName } from 'bundles/author-course/utils/ItemTypeUtils';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { CalendarListItem } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarListItem';
import Pill from 'bundles/degree-home/components/v3-dpm/components/Calendar/Pill';
import { filterOptionValues } from 'bundles/degree-home/components/v3-dpm/components/Calendar/constants/constants';
import { toCamelCase } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils';
import type { UseCalendarEvents_AssignmentEventFragment as AssignmentEvent } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCalendarEvents';
import { convertUnitIntervalGradeToPercentageValue } from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import ItemIcon from 'bundles/item/components/ItemIcon';
import type { ItemType } from 'bundles/item/types/ItemType';
import { truncToPlace } from 'bundles/primitive-formatting';

import _t from 'i18n!nls/degree-home';

const styles = {
  gradeWeight: css`
    display: inline-block;
  `,
  courseName: css`
    a {
      color: inherit !important;
    }
  `,
  dotSeparator: (theme: Theme) => css`
    margin: ${theme.spacing(0, 8)};
  `,
};

const dotSeparator = <span css={styles.dotSeparator}>•</span>;

export const AssignmentEventItem: React.FunctionComponent<{ item: AssignmentEvent }> = ({ item }) => {
  const theme = useTheme();
  const { filter } = useDegreeContext();
  let statusPill;
  const grade = typeof item.grade === 'number' ? convertUnitIntervalGradeToPercentageValue(item.grade) : undefined;
  switch (item.progressState) {
    case AssignmentProgressState.Passed:
      if (grade !== undefined) {
        statusPill = (
          <Pill
            data-testid="grade-pill"
            label={_t('Grade: #{grade}%', { grade })}
            textColor={theme.palette.green[700]}
            backgroundColor={theme.palette.green[100]}
          />
        );
      }
      break;
    case AssignmentProgressState.Failed:
      if (grade !== undefined) {
        statusPill = (
          <Pill
            data-testid="grade-pill"
            label={_t('Grade: #{grade}%', { grade })}
            textColor={theme.palette.green[700]}
            backgroundColor={theme.palette.green[100]}
          />
        );
      }
      break;
    case AssignmentProgressState.Overdue:
      if (item.dueDate) {
        statusPill = (
          <Pill
            data-testid="overdue-pill"
            label={_t('Overdue, #{dueAtTime}', {
              dueAtTime: formatDateTimeDisplay(item.dueDate, MED_DATETIME_NO_YEAR_DISPLAY),
            })}
            textColor={theme.palette.orange[700]}
            backgroundColor={theme.palette.orange[100]}
          />
        );
      }
      break;
    default:
      statusPill = null;
  }

  const subHeading = (
    <span>
      <span css={styles.courseName}>
        <Link
          href={item.courseUrl}
          variant="quiet"
          data-testid={`calendar-list-assignment-item-course-name-link ${item.course.name}`}
        >
          {item.course.name}
        </Link>
      </span>
      {item.progressState === AssignmentProgressState.NotSubmitted && (
        <span>
          {dotSeparator}
          {_t('Due at #{dueAtTime}', { dueAtTime: formatDateTimeDisplay(item.dueDate || '', TIME_ONLY_DISPLAY) })}
        </span>
      )}
      {item.progressState === AssignmentProgressState.Submitted && (
        <span>
          {dotSeparator}
          {_t('Submitted')}
        </span>
      )}
      {typeof item.gradingWeight === 'number' && (
        <span>
          {dotSeparator}
          <span css={styles.gradeWeight} data-testid="calendar-list-item-grade-weight">
            {_t('#{gradingWeight}% Weight', { gradingWeight: truncToPlace(item.gradingWeight * 100, 2) })}
          </span>
        </span>
      )}
    </span>
  );

  const itemIcon = item.isComplete ? (
    <SuccessFilledIcon color="success" size="large" title={_t('Completed')} aria-hidden={false} role="img" />
  ) : (
    <ItemIcon size={24} type={item.assignmentType as ItemType} useCdsIcon={true} cdsIconProps={{ size: 'large' }} />
  );

  const restorableAssignment =
    filter === filterOptionValues.Deadlines && item.progressState !== AssignmentProgressState.Overdue;

  const isRestorable = item.isDismissed && (restorableAssignment || filter === filterOptionValues.Overdue);

  return (
    <CalendarListItem
      itemIcon={itemIcon}
      itemTypeName={getItemTypePrefixTranslationByTypeName(
        toCamelCase(item.assignmentType),
        item.assignmentType === 'staffGraded' || item.assignmentType === 'ungradedAssignment'
          ? {
              isTeamAssignment: item.submissionType === AssignmentSubmissionType.Team,
              isGraded: item.assignmentType === 'staffGraded',
            }
          : undefined
      )}
      itemName={item.assignmentName}
      itemUrl={item.assignmentUrl}
      statusPill={statusPill}
      subHeading={subHeading}
      trackingName="degree_home_assignment_link"
      courseId={item.course.id}
      itemId={item.id}
      isDismissible={filter === filterOptionValues.All && !item.isDismissed}
      isRestorable={isRestorable}
      itemFilterType={
        item.progressState === AssignmentProgressState.Overdue
          ? filterOptionValues.Overdue
          : filterOptionValues.Deadlines
      }
    />
  );
};
